<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-2xl font-bold text-[#22282F]">Workflows prédéfinis</h2>
          <p class="text-sm text-gray-500 mt-1">
            Circuits de validation réutilisables, associables aux types de document
          </p>
        </div>
        <button
          @click="router.push('/workflow-templates/new')"
          class="px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors flex items-center gap-2 shrink-0"
        >
          <Plus class="w-4 h-4" />
          Nouveau workflow
        </button>
      </div>

      <!-- Barre de recherche + filtres -->
      <div class="mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-3">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher un workflow..."
            class="w-full pl-12 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
          />
        </div>
        <select
          v-model="filterStatus"
          class="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] bg-white"
        >
          <option value="all">Tous statuts</option>
          <option value="active">Actifs</option>
          <option value="inactive">Inactifs</option>
        </select>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-16 bg-white border border-gray-200 rounded-xl">
        <div class="w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" />
      </div>

      <!-- Grille de cartes -->
      <div v-else-if="filtered.length" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div
          v-for="wt in filtered"
          :key="wt.id"
          class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4"
        >
          <!-- En-tête de carte -->
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <h3 class="font-semibold text-[#22282F] truncate">{{ wt.nom }}</h3>
                <span
                  :class="[
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0',
                    wt.is_active ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
                  ]"
                >
                  <span :class="['w-1.5 h-1.5 rounded-full', wt.is_active ? 'bg-green-500' : 'bg-red-400']" />
                  {{ wt.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </div>
              <p v-if="wt.description" class="text-xs text-gray-500 mt-1 line-clamp-2">{{ wt.description }}</p>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <button @click="router.push(`/workflow-templates/${wt.id}`)" class="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Modifier">
                <Edit3 class="w-4 h-4 text-gray-500" />
              </button>
              <button @click="askDuplicate(wt)" class="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Dupliquer">
                <Copy class="w-4 h-4 text-gray-500" />
              </button>
              <button @click="askDelete(wt)" class="p-2 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer">
                <Trash2 class="w-4 h-4 text-red-500" />
              </button>
            </div>
          </div>

          <!-- Aperçu du circuit : défilement horizontal, jamais tronqué -->
          <div class="overflow-x-auto -mx-1 px-1">
            <div class="flex items-center gap-1.5 min-w-max py-1">
              <template v-for="(step, idx) in wt.steps" :key="step.id ?? idx">
                <div
                  :class="[
                    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap border',
                    stepChipClass(step)
                  ]"
                >
                  <component :is="stepIcon(step)" class="w-3.5 h-3.5 shrink-0" />
                  {{ stepLabel(step) }}
                </div>
                <ArrowRight v-if="idx < wt.steps.length - 1" class="w-3.5 h-3.5 text-gray-300 shrink-0" />
              </template>
            </div>
          </div>

          <!-- Types de document associés -->
          <div class="flex items-center gap-1.5 flex-wrap pt-3 border-t border-gray-100">
            <span class="text-xs text-gray-400 mr-1">Utilisé par :</span>
            <span
              v-for="dt in wt.document_types"
              :key="dt.id"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
              :title="dt.via_regle ? 'Utilisé par une règle de profil de ce type' : undefined"
            >
              {{ dt.nom }}<template v-if="dt.via_regle">&nbsp;(par profil)</template>
            </span>
            <span v-if="!wt.document_types.length" class="text-xs text-gray-400 italic">Aucun type de document associé</span>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="bg-white border border-gray-200 rounded-xl p-16 text-center shadow-sm">
        <Workflow class="w-14 h-14 text-gray-200 mx-auto mb-4" />
        <h3 class="text-lg font-semibold text-[#22282F] mb-1">Aucun workflow prédéfini</h3>
        <p class="text-sm text-gray-500 mb-5">
          {{ searchQuery || filterStatus !== 'all' ? 'Aucun résultat pour ces filtres' : "Créez un circuit type pour l'associer à un type de document" }}
        </p>
        <button
          v-if="!searchQuery && filterStatus === 'all'"
          @click="router.push('/workflow-templates/new')"
          class="inline-flex items-center gap-2 px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 transition-colors"
        >
          <Plus class="w-4 h-4" /> Créer le premier workflow
        </button>
      </div>

      <!-- Visibilité des types de document globaux -->
      <DocumentTypeVisibilityPanel />
      <DocumentTypeAccessExceptionsPanel />
    </div>

    <!-- Modal confirmation (suppression / duplication) -->
    <div
      v-if="confirmModal.show"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="confirmModal.show = false"
    >
      <div class="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl">
        <div class="flex items-center gap-3 mb-4">
          <div :class="['p-2.5 rounded-full', confirmModal.type === 'delete' ? 'bg-red-100' : 'bg-blue-100']">
            <AlertTriangle v-if="confirmModal.type === 'delete'" class="w-5 h-5 text-red-600" />
            <Copy v-else class="w-5 h-5 text-blue-600" />
          </div>
          <h3 class="text-lg font-bold text-[#22282F]">{{ confirmModal.title }}</h3>
        </div>
        <p class="text-sm text-gray-600 mb-6">{{ confirmModal.message }}</p>
        <div class="flex gap-3">
          <button @click="confirmModal.show = false" class="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Annuler
          </button>
          <button
            @click="confirmModal.onConfirm(); confirmModal.show = false"
            :class="[
              'flex-1 px-4 py-2.5 rounded-lg text-sm text-white transition-colors',
              confirmModal.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#22282F] hover:bg-[#22282F]/90'
            ]"
          >
            {{ confirmModal.confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, reactive } from 'vue'
import { useRouter } from 'vue-router'
import {
  Plus, Search, Edit3, Trash2, Copy, ArrowRight, AlertTriangle,
  Workflow, MapPin, Shield, GitBranch,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import DocumentTypeVisibilityPanel from '@/components/DocumentTypeVisibilityPanel.vue'
import DocumentTypeAccessExceptionsPanel from '@/components/DocumentTypeAccessExceptionsPanel.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import { ROLE_NATIONAL_LABELS, type WorkflowStep, type WorkflowTemplate } from '@/types/workflowTemplate'

const router = useRouter()
const toast = useToast()

const loading = ref(false)
const items = ref<WorkflowTemplate[]>([])
const searchQuery = ref('')
const filterStatus = ref<'all' | 'active' | 'inactive'>('all')

const confirmModal = reactive({
  show: false,
  type: 'delete' as 'delete' | 'duplicate',
  title: '',
  message: '',
  confirmLabel: '',
  onConfirm: () => {},
})

const filtered = computed(() => {
  let list = items.value
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(wt => wt.nom.toLowerCase().includes(q))
  }
  if (filterStatus.value === 'active') list = list.filter(wt => wt.is_active)
  else if (filterStatus.value === 'inactive') list = list.filter(wt => !wt.is_active)
  return list
})

const fetchAll = async () => {
  loading.value = true
  try {
    // La liste n'inclut pas les étapes (steps_count) : on récupère le détail
    // de chacun pour l'aperçu visuel — le nombre de workflows reste modeste.
    const summaries = await apiFetch<WorkflowTemplate[]>('/api/admin/workflow-templates/')
    const details = await Promise.all(
      summaries.map(s => apiFetch<WorkflowTemplate>(`/api/admin/workflow-templates/${s.id}/`))
    )
    items.value = details
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de charger les workflows')
  } finally {
    loading.value = false
  }
}

const stepIcon = (step: WorkflowStep) => {
  if (step.kind === 'HIERARCHIE_INITIATEUR') return GitBranch
  if (step.kind === 'ROLE_NATIONAL') return Shield
  return MapPin
}

const stepChipClass = (step: WorkflowStep) => {
  if (step.kind === 'HIERARCHIE_INITIATEUR') return 'bg-violet-50 text-violet-700 border-violet-100'
  if (step.kind === 'ROLE_NATIONAL') return 'bg-amber-50 text-amber-700 border-amber-100'
  return 'bg-blue-50 text-blue-700 border-blue-100'
}

const stepLabel = (step: WorkflowStep) => {
  if (step.kind === 'HIERARCHIE_INITIATEUR') return "Hiérarchie de l'initiateur"
  if (step.kind === 'ROLE_NATIONAL') return ROLE_NATIONAL_LABELS[step.role_national_code || ''] || step.role_national_code
  return step.target?.label || 'Cible manquante'
}

const askDelete = (wt: WorkflowTemplate) => {
  // Le serveur refuse de supprimer un workflow qu'une règle de profil utilise : on le dit
  // tout de suite plutôt que de faire confirmer une suppression vouée à l'échec.
  const parRegle = wt.document_types.filter(d => d.via_regle)
  if (parRegle.length) {
    toast.error(
      'Suppression impossible',
      `« ${wt.nom} » est utilisé par une règle de profil de : ${parRegle.map(d => d.nom).join(', ')}. Retirez ces règles dans la fiche du type de document avant de le supprimer.`
    )
    return
  }
  confirmModal.type = 'delete'
  confirmModal.title = 'Supprimer ce workflow ?'
  confirmModal.message = wt.document_types.length
    ? `« ${wt.nom} » est utilisé par ${wt.document_types.length} type(s) de document (${wt.document_types.map(d => d.nom).join(', ')}) — ils ne proposeront plus de workflow prédéfini après suppression.`
    : `La suppression de « ${wt.nom} » est irréversible.`
  confirmModal.confirmLabel = 'Supprimer'
  confirmModal.onConfirm = () => doDelete(wt)
  confirmModal.show = true
}

const doDelete = async (wt: WorkflowTemplate) => {
  try {
    await apiFetch(`/api/admin/workflow-templates/${wt.id}/`, { method: 'DELETE' })
    items.value = items.value.filter(w => w.id !== wt.id)
    toast.success('Workflow supprimé', `« ${wt.nom} » a été supprimé`)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de supprimer ce workflow')
  }
}

const askDuplicate = (wt: WorkflowTemplate) => {
  confirmModal.type = 'duplicate'
  confirmModal.title = 'Dupliquer ce workflow ?'
  confirmModal.message = `Une copie « ${wt.nom} (copie) » sera créée avec les mêmes étapes, désactivée par défaut, sans association à un type de document.`
  confirmModal.confirmLabel = 'Dupliquer'
  confirmModal.onConfirm = () => doDuplicate(wt)
  confirmModal.show = true
}

const doDuplicate = async (wt: WorkflowTemplate) => {
  try {
    const clone = await apiFetch<WorkflowTemplate>(`/api/admin/workflow-templates/${wt.id}/duplicate/`, { method: 'POST' })
    router.push(`/workflow-templates/${clone.id}`)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de dupliquer ce workflow')
  }
}

onMounted(fetchAll)
</script>
